import { useEffect, useState } from "react";

import type { County2024 } from "./model";

/* Certified 2024 presidential results by county (public dataset mirrored
   by jsDelivr). Used for the "2024 result" and "shift" county layers and to
   correct the neighbor read. */
const DATASET =
  "tonmcg/US_County_Level_Election_Results_08-24@master/2024_US_County_Level_Presidential_Results.csv";
/* jsDelivr first, GitHub raw as a fallback. */
export const COUNTY_RESULTS_URLS = [
  `https://cdn.jsdelivr.net/gh/${DATASET}`,
  `https://raw.githubusercontent.com/${DATASET.replace("@", "/")}`,
];

/* Alaska reports by state-house district (codes 02001–02040 that collide
   with real borough codes), so it is skipped and falls back to the state
   figure. Connecticut reports by planning region, as the map draws it. */

export const splitCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (const char of line) {
    if (char === '"') quoted = !quoted;
    else if (char === "," && !quoted) {
      cells.push(current);
      current = "";
    } else current += char;
  }
  cells.push(current);
  return cells;
};

/** County FIPS (as drawn on the map) → Harris share of the two-party vote. */
export const parseCountyCsv = (text: string): County2024 => {
  const [header, ...lines] = text.trim().split(/\r?\n/);
  const columns = splitCsvLine(header).map((c) => c.trim().toLowerCase());
  const fipsAt = columns.findIndex((c) => c.includes("fips"));
  const demAt = columns.findIndex((c) => c === "votes_dem");
  const gopAt = columns.findIndex((c) => c === "votes_gop");
  if (fipsAt < 0 || demAt < 0 || gopAt < 0) {
    throw new Error("Unexpected 2024 county results format");
  }

  const votes: Record<string, { dem: number; gop: number }> = {};
  for (const line of lines) {
    const cells = splitCsvLine(line);
    const code = cells[fipsAt]?.trim().padStart(5, "0");
    const dem = Number(cells[demAt]);
    const gop = Number(cells[gopAt]);
    if (!code || code.startsWith("02") || !(dem + gop > 0)) continue;
    const total = (votes[code] ??= { dem: 0, gop: 0 });
    total.dem += dem;
    total.gop += gop;
  }
  return Object.fromEntries(
    Object.entries(votes).map(([fips, v]) => [fips, v.dem / (v.dem + v.gop)])
  );
};

let cache: Promise<County2024> | null = null;

const useCounty2024 = () => {
  const [county2024, setCounty2024] = useState<County2024 | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<County2024> => {
      let failure = "";
      for (const url of COUNTY_RESULTS_URLS) {
        try {
          const response = await fetch(url);
          if (response.ok) return parseCountyCsv(await response.text());
          failure = `HTTP ${response.status}`;
        } catch (e) {
          failure = e instanceof Error ? e.message : String(e);
        }
      }
      throw new Error(failure);
    };
    cache ??= load();
    cache
      .then((data) => {
        if (!cancelled) setCounty2024(data);
      })
      .catch((e: unknown) => {
        cache = null;
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { county2024, error };
};

export default useCounty2024;
